/**
 * UncatalogedCoverageFooter — combined uncataloged inventory + staleness
 * disclosure footer.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §D
 *
 * Renders a single load-bearing disclosure block at the bottom of the
 * Inventory tab combining:
 *   - Per P2 §D.1 N2 lock: section footer placement (NOT top banner /
 *     NOT sidebar card)
 *   - Per P2 §D.2 N3 lock: static text v1 (no tooltip / no modal)
 *   - Per P2 §D.3: $X cataloged of $Y total; Z% uncataloged
 *   - Per P2 §D.4 inherited from §I.1 P2 §C.2.1: staleness disclosure
 *     "Coverage reflects last inventory ingest on YYYY-MM-DD (Z days ago)"
 *
 * Renders nothing when uncataloged data is absent (empty-state cases
 * handled by parent component's no-data branch).
 */
import React from 'react';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so this footer flips with the shell in the future dark
// lane. It keeps its subtle panel-2 fill + hair border.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

function formatCurrencyM(v) {
  if (v === null || v === undefined) return 'Not available';
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatSnapshotAge(days) {
  if (days === null || days === undefined) return '';
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function UncatalogedCoverageFooter({ uncataloged, snapshotDate, snapshotAgeDays }) {
  const u = uncataloged || {};
  if (
    u.cataloged_dollar === null || u.cataloged_dollar === undefined
    || u.total_dollar === null || u.total_dollar === undefined
  ) {
    return null;
  }

  const cataloged = u.cataloged_dollar;
  const total = u.total_dollar;
  const uncatalogedDollar = u.uncataloged_dollar;
  const supplierAttributablePct = u.supplier_attributable_pct;
  const uncatalogedPct = u.uncataloged_pct;

  return (
    <div
      data-testid="uncataloged-coverage-footer"
      style={{
        ...LINEAR_PANEL,
        background: 'var(--panel-2)',
        border: '1px solid var(--hair)',
        padding: '14px 20px',
        marginTop: '8px',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)',
          marginBottom: '6px',
        }}
      >
        Coverage
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          lineHeight: 1.5,
        }}
      >
        <strong>{formatCurrencyM(cataloged)}</strong> of{' '}
        <strong>{formatCurrencyM(total)}</strong> total inventory is
        supplier-attributable
        {supplierAttributablePct !== null && supplierAttributablePct !== undefined
          ? ` (${supplierAttributablePct.toFixed(1)}%)`
          : ''}
        . The remaining <strong>{formatCurrencyM(uncatalogedDollar)}</strong>
        {uncatalogedPct !== null && uncatalogedPct !== undefined
          ? ` (${uncatalogedPct.toFixed(1)}%)`
          : ''}{' '}
        is uncataloged: material numbers in stock that are not yet listed
        in <code style={{ fontFamily: 'monospace' }}>materials_master</code>.
        {snapshotDate && (
          <>
            {' '}
            Coverage reflects last inventory ingest on{' '}
            <strong>{snapshotDate}</strong>
            {snapshotAgeDays !== null && snapshotAgeDays !== undefined
              ? ` (${formatSnapshotAge(snapshotAgeDays)})`
              : ''}
            . Materials added to the catalog since then may not yet be
            attributed to a supplier.
          </>
        )}
      </div>
    </div>
  );
}

export default UncatalogedCoverageFooter;
